import { type JSX, type ReactNode, createContext, use } from 'react'

/**
 * Whether the screen is a read-only display.
 *
 * The default is false, so a console screen stays editable. Only
 * `ReadOnlyProvider` turns it on. The signal is not the URL.
 */
const ReadOnlyContext = createContext(false)

/**
 * Whether a scope value sits in a `ScopeLine`. There it drops its label.
 */
const ScopeLineContext = createContext(false)

/**
 * Whether this screen is read-only.
 *
 * A display provides true. Every other screen gets false.
 *
 * @returns True on a display. False on the console.
 */
export function useReadOnly(): boolean {
  return use(ReadOnlyContext)
}

/**
 * Mark the screen as read-only.
 *
 * The display runtime wraps the page it shows. A console screen does not.
 *
 * @param props - Provider props.
 * @param props.children - The page.
 * @returns The provider.
 */
export function ReadOnlyProvider({
  children
}: {
  children: ReactNode
}): JSX.Element {
  return <ReadOnlyContext value={true}>{children}</ReadOnlyContext>
}

/**
 * A report's scope as one line of values.
 *
 * A TV has no room for a labelled control per value. Inside this line every
 * `ReadOnlyValue` and `ScopeItem` shows its value only, a dot between each
 * two. The `scope-line` utility in `index.css` lays the line out.
 *
 * @param props - Line props.
 * @param props.testId - The element's `data-testid`.
 * @param props.children - The scope controls, in read-only mode.
 * @returns The line.
 */
export function ScopeLine({
  testId,
  children
}: {
  testId: string
  children: ReactNode
}): JSX.Element {
  return (
    <ScopeLineContext value={true}>
      <div data-testid={testId} className="overflow-hidden">
        <div className="scope-line">{children}</div>
      </div>
    </ScopeLineContext>
  )
}

/**
 * One value of a report's scope, as a sentence.
 *
 * In a `ScopeLine` it is one item of the line. Elsewhere it is a paragraph.
 *
 * @param props - Item props.
 * @param props.testId - The element's `data-testid`.
 * @param props.children - The value.
 * @returns The value.
 */
export function ScopeItem({
  testId,
  children
}: {
  testId: string
  children: ReactNode
}): JSX.Element {
  const inLine = use(ScopeLineContext)

  return inLine ? (
    <span data-testid={testId} data-scope-item="">
      {children}
    </span>
  ) : (
    <p data-testid={testId}>{children}</p>
  )
}

/**
 * A scope value with no control.
 *
 * A display shows what is in force. It does not offer a way to change it.
 * In a `ScopeLine` the label drops, and the value is one item of the line.
 *
 * @param props - The label and the value.
 * @param props.testId - The element's `data-testid`. The value adds `-value`.
 * @param props.label - The name of the value.
 * @param props.value - The value in force.
 * @returns The label and the value.
 */
export function ReadOnlyValue({
  testId,
  label,
  value
}: {
  testId: string
  label: ReactNode
  value: ReactNode
}): JSX.Element {
  const inLine = use(ScopeLineContext)
  if (inLine) {
    return (
      <span data-testid={testId} data-scope-item="">
        <span data-testid={`${testId}-value`}>{value}</span>
      </span>
    )
  }

  return (
    <div data-testid={testId} className="grid gap-1">
      <span className="type-eyebrow">{label}</span>
      <p data-testid={`${testId}-value`}>{value}</p>
    </div>
  )
}
